import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FaLungs, FaBrain, FaCamera, FaPlus, FaClipboardList, FaUserMd, FaChartLine, FaHeartbeat, FaEye, FaWalking, FaBaby, FaLayerGroup, FaArrowRight } from 'react-icons/fa';
import { getUserHistory } from '../utils/api';

const TYPE_LABEL = {
  xray: 'Chest X-Ray',
  breast: 'Breast Ultrasound',
  skin: 'Skin',
  retina: 'Retinal Fundus',
  foot: 'Diabetic Foot',
  fetal_plane: 'Fetal Ultrasound',
};

const TYPE_ICON = {
  xray: <FaLungs className="text-indigo-400" />,
  breast: <FaBrain className="text-purple-400" />,
  skin: <FaCamera className="text-blue-400" />,
  retina: <FaEye className="text-teal-400" />,
  foot: <FaWalking className="text-amber-400" />,
};

const NORMAL_RISK_TIERS = new Set(['normal', 'benign', 'none']);

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getUserHistory(currentUser?.token);
        if (cancelled) return;
        setHistory([...data].sort((a, b) => new Date(b.date) - new Date(a.date)));
      } catch {
        // Silently show empty state — the Analyses page surfaces the real error.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = history.filter((a) => {
      const d = new Date(a.date);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    const abnormal = history.filter((a) => {
      const top = a.predictions?.[0];
      if (!top) return false;
      const risk = (top.risk || '').toLowerCase();
      const label = (top.label || '').toLowerCase();
      return !NORMAL_RISK_TIERS.has(risk) && label !== 'no finding';
    }).length;
    const typesUsed = new Set(history.map((a) => a.type)).size;
    return [
      { name: 'Total Analyses', value: String(history.length), icon: <FaClipboardList className="h-5 w-5 text-purple-400" /> },
      { name: 'This Month', value: String(thisMonth), icon: <FaChartLine className="h-5 w-5 text-indigo-400" /> },
      { name: 'Abnormal Findings', value: String(abnormal), icon: <FaUserMd className="h-5 w-5 text-red-400" /> },
      { name: 'Types Used', value: `${typesUsed}/5`, icon: <FaHeartbeat className="h-5 w-5 text-green-400" /> },
    ];
  }, [history]);

  const recentAnalyses = history.slice(0, 3);

  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Navbar />
      <main className="flex-grow py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="heading-secondary">Welcome back, {currentUser?.name || 'User'}</h1>
            <p className="text-gray-400 mt-1">Here's what's happening with your account</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {stats.map((stat, index) => (
              <div key={index} className="bg-gray-900 rounded-2xl shadow-md p-6 hover:shadow-lg transition-shadow duration-300 border border-gray-800">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 rounded-xl bg-gray-800">
                    {stat.icon}
                  </div>
                  <span className="text-3xl font-bold text-purple-400">{loading ? '—' : stat.value}</span>
                </div>
                <div className="text-sm font-medium text-gray-400">{stat.name}</div>
              </div>
            ))}
          </div>

          {/* Screening encounter — the primary field workflow. A health worker
              screens both eyes and both feet in one visit; giving that its own
              full-width entry point rather than a sixth tile in the modality
              grid reflects that it is a different shape of task, not another
              single-image analysis. */}
          <Link
            to="/analyze/encounter"
            className="group block mb-6 rounded-2xl overflow-hidden border border-purple-700/60 bg-gradient-to-r from-purple-900/50 to-indigo-900/40 hover:border-purple-500 transition-all duration-300"
          >
            <div className="p-6 flex items-center gap-5">
              <div className="p-4 bg-purple-900/50 rounded-full group-hover:bg-purple-800/60 transition-colors shrink-0">
                <FaLayerGroup className="h-8 w-8 text-purple-300" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-white text-lg">Screening encounter</h3>
                  <span className="px-2 py-0.5 text-xs bg-purple-900/60 text-purple-200 border border-purple-600/50 rounded-full">
                    New
                  </span>
                </div>
                <p className="text-sm text-gray-300 mt-1">
                  Several images from one patient visit — both eyes and both feet —
                  read together, with your clinical notes. Returns one triage
                  decision, plus left/right comparison and patterns across organs.
                </p>
                <p className="text-xs text-purple-300/80 mt-2">
                  Capture straight from the camera or upload files
                </p>
              </div>
              <FaArrowRight className="h-5 w-5 text-purple-400 group-hover:translate-x-1 transition-transform shrink-0" />
            </div>
          </Link>

          {/* Quick Actions */}
          <div className="bg-gray-900 rounded-2xl shadow-md overflow-hidden mb-10 border border-gray-800">
            <div className="p-6 bg-gradient-to-r from-purple-900 to-indigo-900 text-white border-b border-gray-800">
              <h2 className="heading-subsection">Quick Actions</h2>
              <p className="text-indigo-200 mt-1">Start a new analysis or view your reports</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
                <Link
                  to="/analyze/xray"
                  className="group flex flex-col items-center p-6 bg-black rounded-xl border border-gray-800 hover:border-indigo-700 transition-all duration-300"
                >
                  <div className="p-4 bg-indigo-900/30 rounded-full mb-4 group-hover:bg-indigo-900/50 transition-colors duration-300">
                    <FaLungs className="h-8 w-8 text-indigo-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 text-center">Chest X-Ray</h3>
                  <p className="text-sm text-gray-400 text-center">Analyze a chest X-ray</p>
                </Link>

                <Link
                  to="/analyze/ct-scan"
                  className="group flex flex-col items-center p-6 bg-black rounded-xl border border-gray-800 hover:border-purple-700 transition-all duration-300"
                >
                  <div className="p-4 bg-purple-900/30 rounded-full mb-4 group-hover:bg-purple-900/50 transition-colors duration-300">
                    <FaBrain className="h-8 w-8 text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 text-center">Breast Ultrasound</h3>
                  <p className="text-sm text-gray-400 text-center">Analyze a breast ultrasound</p>
                </Link>

                <Link
                  to="/analyze/skin"
                  className="group flex flex-col items-center p-6 bg-black rounded-xl border border-gray-800 hover:border-blue-700 transition-all duration-300"
                >
                  <div className="p-4 bg-blue-900/30 rounded-full mb-4 group-hover:bg-blue-900/50 transition-colors duration-300">
                    <FaCamera className="h-8 w-8 text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 text-center">Skin Lesion</h3>
                  <p className="text-sm text-gray-400 text-center">Analyze a skin lesion</p>
                </Link>

                <Link
                  to="/analyze/retina"
                  className="group flex flex-col items-center p-6 bg-black rounded-xl border border-gray-800 hover:border-teal-700 transition-all duration-300"
                >
                  <div className="p-4 bg-teal-900/30 rounded-full mb-4 group-hover:bg-teal-900/50 transition-colors duration-300">
                    <FaEye className="h-8 w-8 text-teal-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 text-center">Retinal Fundus</h3>
                  <p className="text-sm text-gray-400 text-center">Multi-disease retina screening</p>
                  <span className="mt-2 px-2 py-0.5 text-xs bg-teal-900/40 text-teal-300 border border-teal-700/40 rounded-full">New</span>
                </Link>

                <Link
                  to="/analyze/foot"
                  className="group flex flex-col items-center p-6 bg-black rounded-xl border border-gray-800 hover:border-amber-700 transition-all duration-300"
                >
                  <div className="p-4 bg-amber-900/30 rounded-full mb-4 group-hover:bg-amber-900/50 transition-colors duration-300">
                    <FaWalking className="h-8 w-8 text-amber-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 text-center">Diabetic Foot</h3>
                  <p className="text-sm text-gray-400 text-center">Classify foot ulcer type</p>
                  <span className="mt-2 px-2 py-0.5 text-xs bg-amber-900/40 text-amber-300 border border-amber-700/40 rounded-full">New</span>
                </Link>

                <Link
                  to="/analyze/fetal-plane"
                  className="group flex flex-col items-center p-6 bg-black rounded-xl border border-gray-800 hover:border-pink-700 transition-all duration-300"
                >
                  <div className="p-4 bg-pink-900/30 rounded-full mb-4 group-hover:bg-pink-900/50 transition-colors duration-300">
                    <FaBaby className="h-8 w-8 text-pink-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 text-center">Fetal Ultrasound</h3>
                  <p className="text-sm text-gray-400 text-center">Check the scan captured the right plane</p>
                  <span className="mt-2 px-2 py-0.5 text-xs bg-pink-900/40 text-pink-300 border border-pink-700/40 rounded-full">New</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Recent Analyses */}
          <div className="bg-gray-900 rounded-2xl shadow-md overflow-hidden mb-10 border border-gray-800">
            <div className="p-6 border-b border-gray-800 flex justify-between items-center">
              <h2 className="heading-subsection">Recent Analyses</h2>
              <Link to="/analysis" className="text-sm font-medium text-purple-400 hover:text-purple-300 flex items-center">
                View all
                <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>

            {loading && <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>}

            {!loading && recentAnalyses.length === 0 && (
              <div className="p-10 text-center text-gray-400 text-sm">
                No analyses yet — run one from Quick Actions above.
              </div>
            )}

            {!loading && recentAnalyses.length > 0 && (
              <div className="divide-y divide-gray-800">
                {recentAnalyses.map((analysis) => {
                  const top = analysis.predictions?.[0];
                  const confidencePct = top ? Math.round((top.confidence || 0) * 100) : null;
                  const riskTier = (top?.risk || '').toLowerCase();
                  const isNormal = !top || NORMAL_RISK_TIERS.has(riskTier) || (top.label || '').toLowerCase() === 'no finding';
                  const dateLabel = analysis.date
                    ? new Date(analysis.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                    : 'Unknown date';

                  return (
                    <div key={analysis.id} className="p-6 hover:bg-gray-800/30 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          <div className="p-3 bg-gray-800 rounded-xl">
                            {TYPE_ICON[analysis.type] || <FaClipboardList className="text-gray-400" />}
                          </div>
                          <div>
                            <div className="flex items-center space-x-3">
                              <h3 className="heading-card">{TYPE_LABEL[analysis.type] || analysis.type}</h3>
                            </div>
                            <p className="text-sm text-gray-400 mt-1">
                              Analyzed on {dateLabel}
                            </p>
                            <div className="mt-2">
                              <div className="flex items-center space-x-2">
                                <div className={`h-2 w-2 rounded-full ${isNormal ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                <p className={`text-sm font-medium ${isNormal ? 'text-green-400' : 'text-red-400'}`}>
                                  {top?.label || 'No prediction recorded'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <div className="mb-2">
                            <div className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              isNormal ? 'bg-green-900/30 text-green-400 border border-green-500/30' :
                              'bg-red-900/30 text-red-400 border border-red-500/30'
                            }`}>
                              {isNormal ? 'Normal' : 'Abnormal'}
                            </div>
                          </div>
                          {confidencePct != null && (
                            <div className="flex items-center mt-4">
                              <span className="text-xs text-gray-400 mr-2">AI confidence:</span>
                              <div className="w-16 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${
                                    confidencePct > 70 ? 'bg-green-500' :
                                    confidencePct > 40 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${confidencePct}%` }}
                                ></div>
                              </div>
                              <span className="ml-2 text-xs text-gray-400">{confidencePct}%</span>
                            </div>
                          )}
                          <Link to={`/analysis/${analysis.id}`} className="mt-4 text-purple-400 hover:text-purple-300 text-sm font-medium flex items-center">
                            View Details
                            <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="bg-black/30 px-6 py-4 border-t border-gray-800">
              <Link to="/analyze/xray" className="text-purple-400 hover:text-purple-300 text-sm font-medium flex items-center">
                <FaPlus className="mr-2 h-4 w-4" />
                Start New Analysis
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Dashboard;
